import { createContext, useContext, useState, useEffect, useLayoutEffect, useCallback, useRef, type ReactNode } from 'react';
import type { PublicClientApplication, AccountInfo } from '@azure/msal-browser';
import * as Sentry from '@sentry/react';
import { api, configureCookieFirstAuth } from '../api/http';
import { logoutDevice, resetDeviceInstallation } from '../api/device';
import {
  clearInteractiveMicrosoftLoginStarted,
  consumeInteractiveMicrosoftReauth,
  markInteractiveMicrosoftLoginStarted,
  type AuthConfig,
} from './msalConfig';
import {
  invalidateLocalAuthGeneration,
  MicrosoftBridgeCoordinator,
  resolveSafeAuthReturnPath,
} from './cookieFirstAuth';
import { isExpectedSilentAuthError } from './silentAuthError';
import { stashPushRedirectForAuth } from '../utils/swNavigationBridge';
import { usePushSubscription } from '../hooks/usePushSubscription';
import { appSurfaceForPath } from '../appSurface';
import { rememberLegacyAlertRetirement } from '../utils/legacyAlertRetirement';

export type LogoutOutcome = 'logged_out' | 'device_registration_required' | 'failed';

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  authEnabled: boolean;
  user: AccountInfo | null;
  login: (options?: { explicitReauth?: boolean }) => void;
  logout: () => Promise<LogoutOutcome>;
  logoutIssue: Exclude<LogoutOutcome, 'logged_out'> | null;
  clearLogoutIssue: () => void;
  resetDevice: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
  authMethods: string[];
  markNativeAuthenticated: () => void;
  markNativeUnauthenticated: () => void;
  activeAuthMethod: 'microsoft' | 'native' | null;
  nativeRemember: boolean;
  idleMinutes: number;
}

const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  isLoading: true,
  authEnabled: false,
  user: null,
  login: () => {},
  logout: async () => 'failed',
  logoutIssue: null,
  clearLogoutIssue: () => {},
  resetDevice: async () => {},
  getAccessToken: async () => null,
  authMethods: [],
  markNativeAuthenticated: () => {},
  markNativeUnauthenticated: () => {},
  activeAuthMethod: null,
  nativeRemember: false,
  idleMinutes: 60,
});

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => useContext(AuthContext);

const LOGIN_SCOPES = ['openid', 'profile', 'email'];

// Refresh tokens 5 minutes before they expire
const REFRESH_BUFFER_MS = 5 * 60 * 1000;

/** Decode JWT payload without validation (for reading exp claim client-side). */
function parseJwtExp(token: string): number | null {
  try {
    const payload = token.split('.')[1];
    const decoded = JSON.parse(atob(payload));
    return typeof decoded.exp === 'number' ? decoded.exp : null;
  } catch {
    return null;
  }
}

/**
 * P3-A1: mint the bridge cookie right after MSAL resolves a cached account.
 *
 * PP9: takes `getAccessToken` directly (AuthProvider's own closure) instead
 * of routing through the shared `api` client's module-level token-getter
 * (wired up later, in App.tsx's own effect). That avoids a race entirely —
 * this call needs no ordering guarantee against App.tsx's `setTokenGetter`
 * effect because it never depends on it.
 */
async function bridgeSessionAfterMicrosoftLogin(
  getAccessToken: () => Promise<string | null>,
  options: { explicitReauth?: boolean; idToken?: string } = {},
): Promise<boolean> {
  try {
    const token = options.idToken ?? await getAccessToken();
    if (!token) return false;
    const { isStandaloneDisplayMode } = await import('../utils/displayMode');
    await api.post(
      '/auth/session/bridge',
      {
        display_mode: isStandaloneDisplayMode() ? 'standalone' : 'browser',
        explicit_reauth: options.explicitReauth === true,
      },
      { headers: { Authorization: `Bearer ${token}` } },
    );
    return true;
  } catch {
    // Best-effort — a failed bridge call just means the NEXT app-open falls
    // back to MSAL's normal (shorter) silent-refresh path. Never blocks render.
    return false;
  }
}

interface AuthProviderProps {
  msalInstance: PublicClientApplication | null;
  config: AuthConfig;
  children: ReactNode;
}

export function AuthProvider({ msalInstance, config, children }: AuthProviderProps) {
  const microsoftActive = !!msalInstance;
  const cookieFirstEnabled = microsoftActive && config.cookie_first_enabled === true;
  const authMethods: string[] = config.methods ?? (config.enabled ? ['microsoft'] : []);
  const nativeActive = authMethods.includes('native');

  // null = still probing, true = authenticated, false = not authenticated
  const [nativeAuthed, setNativeAuthed] = useState<boolean | null>(nativeActive ? null : false);
  const [isLoading, setIsLoading] = useState(microsoftActive || nativeActive);
  const [user, setUser] = useState<AccountInfo | null>(null);
  const [microsoftCookieAuthed, setMicrosoftCookieAuthed] = useState(false);
  const [activeAuthMethod, setActiveAuthMethod] = useState<'microsoft' | 'native' | null>(null);
  const [nativeRemember, setNativeRemember] = useState(false);
  const [logoutIssue, setLogoutIssue] = useState<Exclude<LogoutOutcome, 'logged_out'> | null>(null);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const bridgeCoordinatorRef = useRef<MicrosoftBridgeCoordinator | null>(null);
  const resetCommittedRef = useRef(false);
  const surface = appSurfaceForPath(window.location.pathname);
  const { unsubscribeDeviceRegistrations } = usePushSubscription(surface);

  // On mount, independently resolve the Microsoft bridge cookie and optional
  // MSAL account. A valid bridge remains authenticated even when MSAL's local
  // account cache has been lost.
  useLayoutEffect(() => {
    let disposed = false;

    const initialize = async () => {
      if (microsoftActive) {
        const interactiveReauth = consumeInteractiveMicrosoftReauth(msalInstance);
        const accounts = msalInstance.getAllAccounts();
        if (accounts.length > 0) {
          msalInstance.setActiveAccount(accounts[0]);
          setUser(accounts[0]);
        }

        if (cookieFirstEnabled) {
          const coordinator = new MicrosoftBridgeCoordinator(msalInstance, {
            onRemoteLogout: () => {
              setMicrosoftCookieAuthed(false);
              setActiveAuthMethod(current => current === 'microsoft' ? null : current);
              setUser(null);
            },
            onInteractionRequired: () => {
              stashPushRedirectForAuth(window.location.search, surface);
              void msalInstance.loginRedirect({ scopes: LOGIN_SCOPES }).catch(error => {
                Sentry.captureException(error, { tags: { source: 'cookie_auth_interactive' } });
              });
            },
          });
          bridgeCoordinatorRef.current = coordinator;
          configureCookieFirstAuth(true, async () => {
            const authenticated = await coordinator.renew();
            if (authenticated && !disposed) {
              setMicrosoftCookieAuthed(true);
              setActiveAuthMethod('microsoft');
            }
            return authenticated;
          });

          let authenticated = interactiveReauth?.idToken
            ? await bridgeSessionAfterMicrosoftLogin(getAccessToken, {
              explicitReauth: true,
              idToken: interactiveReauth.idToken,
            })
            : false;
          if (!authenticated) authenticated = await coordinator.probe();
          if (!authenticated && accounts.length > 0) authenticated = await coordinator.renew();
          if (!disposed && authenticated) {
            setMicrosoftCookieAuthed(true);
            setActiveAuthMethod('microsoft');
          }
        } else {
          // Cookie-first is advertised off (`/auth/config`'s cookie_first_enabled),
          // which is the same setting that gates POST /auth/session/bridge on the
          // server — so a bridge call here could only ever 404. Skip it and stay on
          // MSAL's own silent-refresh path, which is what this deployment uses.
          configureCookieFirstAuth(false);
          if (accounts.length > 0) setActiveAuthMethod('microsoft');
        }
      } else {
        configureCookieFirstAuth(false);
      }

      if (nativeActive) {
        try {
          // Cookie-only native probe: a Microsoft bearer must never relabel
          // its user as native in a dual-auth deployment.
          const response = await fetch('/api/auth/native/session', { credentials: 'include' });
          const data = response.ok ? await response.json() : null;
          if (!disposed && data?.authenticated) {
            setNativeAuthed(true);
            setActiveAuthMethod('native');
            setNativeRemember(Boolean(data.session_remember));
          }
        } catch { /* not native */ }
      }
      if (!disposed) setIsLoading(false);
    };

    void initialize();
    return () => {
      disposed = true;
      bridgeCoordinatorRef.current?.dispose();
      bridgeCoordinatorRef.current = null;
      configureCookieFirstAuth(false);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Schedule a proactive token refresh before the current token expires
  const scheduleRefresh = useCallback((idToken: string) => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    const exp = parseJwtExp(idToken);
    if (!exp) return;

    const expiresAt = exp * 1000; // ms
    const refreshAt = expiresAt - REFRESH_BUFFER_MS;
    const delay = refreshAt - Date.now();

    if (delay <= 0) {
      // Token already within refresh window — refresh immediately
      console.info('Token within refresh window, refreshing now');
      silentRefresh();
      return;
    }

    console.info(`Scheduling proactive token refresh in ${Math.round(delay / 1000)}s`);
    refreshTimerRef.current = setTimeout(() => {
      refreshTimerRef.current = null;
      silentRefresh();
    }, delay);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [msalInstance, config.enabled]);

  // Perform a silent token refresh (called proactively before expiry)
  const silentRefresh = useCallback(async () => {
    if (!msalInstance || !config.enabled || cookieFirstEnabled) return;
    const account = msalInstance.getActiveAccount();
    if (!account) return;

    try {
      const result = await msalInstance.acquireTokenSilent({
        scopes: LOGIN_SCOPES,
        account,
        forceRefresh: true,
      });
      if (result.idToken) {
        console.info('Proactive token refresh succeeded');
        scheduleRefresh(result.idToken);
      }
    } catch (err) {
      console.warn('Proactive token refresh failed:', err);
      if (!isExpectedSilentAuthError(err)) {
        Sentry.captureException(err, { tags: { source: 'token_refresh' } });
      }
      // Don't redirect here — the next API call will trigger the 401 flow
    }
  }, [msalInstance, config.enabled, cookieFirstEnabled, scheduleRefresh]);

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, []);

  const login = useCallback(async (options: { explicitReauth?: boolean } = {}) => {
    if (!msalInstance) return;
    // Cancel any pending proactive refresh — it would race with the redirect.
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    // Fix-batch item 5 (Task-6 review): loginRedirect() navigates this tab
    // away to login.microsoftonline.com and back to a bare-origin
    // redirectUri, dropping any `?pushRedirect=` query string a push
    // notification's fresh window opened with. Stash the (validated)
    // target in sessionStorage now, while it's still in window.location —
    // useSwNavigationBridge consumes it once auth resolves and the Router
    // mounts. See swNavigationBridge.ts's doc comment for the full picture.
    stashPushRedirectForAuth(window.location.search, surface);
    if (options.explicitReauth === true) markInteractiveMicrosoftLoginStarted();

    // MSAL keeps an "interaction in progress" flag in sessionStorage. When a
    // prior redirect got interrupted (most often after an overnight token
    // expiry), the flag sticks and loginRedirect throws interaction_in_progress
    // — the app sits silently stuck until the user fully closes the browser
    // (which is the only thing that clears sessionStorage). Ctrl+Shift+R
    // doesn't clear sessionStorage, which is why a hard reload didn't help.
    // Catch the stuck state, wipe MSAL's session keys, and retry.
    try {
      await msalInstance.loginRedirect({ scopes: LOGIN_SCOPES });
    } catch (err) {
      const code = (err as { errorCode?: string })?.errorCode;
      if (code === 'interaction_in_progress') {
        for (const key of Object.keys(sessionStorage)) {
          if (key.startsWith('msal.')) sessionStorage.removeItem(key);
        }
        try {
          await msalInstance.loginRedirect({ scopes: LOGIN_SCOPES });
          return;
        } catch (retryErr) {
          if (options.explicitReauth === true) clearInteractiveMicrosoftLoginStarted();
          Sentry.captureException(retryErr, { tags: { source: 'login_retry' } });
        }
      } else {
        if (options.explicitReauth === true) clearInteractiveMicrosoftLoginStarted();
        Sentry.captureException(err, { tags: { source: 'login' } });
      }
      throw err;
    }
  }, [msalInstance, surface]);

  const logout = useCallback(async (): Promise<LogoutOutcome> => {
    setLogoutIssue(null);
    const cookieCoordinator = activeAuthMethod === 'microsoft' && cookieFirstEnabled
      ? bridgeCoordinatorRef.current
      : null;
    // Generation invalidation must happen before push unsubscribe or any other
    // awaited logout work. Cookie revocation remains after push unsubscribe so
    // the backend can still identify and remove this user's subscription.
    if (cookieCoordinator) cookieCoordinator.beginLogout();
    else invalidateLocalAuthGeneration();

    // Gemini M (whole-branch review): a device that never unsubscribes its
    // push registration on logout keeps receiving urgent-task pushes for
    // whoever logs in NEXT on that same device/browser — the server-side
    // session is gone, but the browser subscription + DB row survive.
    // Best-effort and MUST run before the session is torn down below: the
    // Snapshot both exact endpoints before best-effort browser/API cleanup.
    // If enumeration itself is unresolved, do not claim a secure device
    // logout: the server cannot safely distinguish this browser's nullable
    // legacy rows from another pre-migration phone.
    let pushProof;
    try {
      pushProof = await unsubscribeDeviceRegistrations();
    } catch (error) {
      Sentry.captureException(error, { tags: { source: 'device_push_enumeration' } });
      setLogoutIssue('failed');
      return 'failed';
    }

    // This authenticated request is decisive: it fences sessions and exact
    // push rows for this device. With resolved endpoint proof it also retires
    // only matching same-user legacy rows; unresolved proof uses the broader,
    // explicitly signaled same-user legacy-alert fallback.
    try {
      const legacyAlertsRetired = await logoutDevice(pushProof);
      if (legacyAlertsRetired) rememberLegacyAlertRetirement();
    } catch (error) {
      // Browser cleanup is best effort, but the authenticated device fence is
      // the logout security boundary. Keep this surface signed in so the user
      // can retry rather than presenting a successful logout while live
      // server sessions may remain.
      Sentry.captureException(error, { tags: { source: 'device_logout' } });
      const status = (error as { response?: { status?: number } }).response?.status;
      const issue = status === 400 ? 'device_registration_required' : 'failed';
      setLogoutIssue(issue);
      return issue;
    }

    const currentReturn = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    const returnPath = resolveSafeAuthReturnPath(surface, currentReturn);

    if (activeAuthMethod === 'native') {
      setNativeAuthed(false);
      setActiveAuthMethod(null);
      window.location.assign(returnPath);
      return 'logged_out';
    }
    if (activeAuthMethod === 'microsoft' && msalInstance) {
      if (refreshTimerRef.current) { clearTimeout(refreshTimerRef.current); refreshTimerRef.current = null; }
      // The server device fence above is decisive for Valentine sessions;
      // MSAL logout is decisive for the same browser's remaining bearer
      // account. Do not report success if that local Microsoft step rejects.
      try {
        await msalInstance.logoutRedirect({
          postLogoutRedirectUri: new URL(returnPath, window.location.origin).toString(),
        });
      } catch (error) {
        Sentry.captureException(error, { tags: { source: 'microsoft_logout_redirect' } });
        setLogoutIssue('failed');
        return 'failed';
      }
      setMicrosoftCookieAuthed(false);
      setUser(null);
      setActiveAuthMethod(null);
      return 'logged_out';
    }
    return 'logged_out';
  }, [activeAuthMethod, cookieFirstEnabled, msalInstance, surface, unsubscribeDeviceRegistrations]);

  const resetDevice = useCallback(async (): Promise<void> => {
    setLogoutIssue(null);
    const cookieCoordinator = activeAuthMethod === 'microsoft' && cookieFirstEnabled
      ? bridgeCoordinatorRef.current
      : null;
    if (!resetCommittedRef.current) {
      if (cookieCoordinator) cookieCoordinator.beginLogout();
      else invalidateLocalAuthGeneration();
      let pushProof;
      try {
        pushProof = await unsubscribeDeviceRegistrations();
      } catch (error) {
        setLogoutIssue('failed');
        throw error;
      }
      const legacyAlertsRetired = await resetDeviceInstallation(pushProof);
      if (legacyAlertsRetired) rememberLegacyAlertRetirement();
      // A rejected MSAL redirect can be retried locally without rotating and
      // fencing another replacement identity or requiring the revoked cookie.
      resetCommittedRef.current = true;
    }
    const currentReturn = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    const returnPath = resolveSafeAuthReturnPath(surface, currentReturn);
    if (activeAuthMethod === 'microsoft' && msalInstance) {
      await msalInstance.logoutRedirect({
        postLogoutRedirectUri: new URL(returnPath, window.location.origin).toString(),
      });
      setNativeAuthed(false);
      setMicrosoftCookieAuthed(false);
      setUser(null);
      setActiveAuthMethod(null);
      return;
    }
    setNativeAuthed(false);
    setMicrosoftCookieAuthed(false);
    setUser(null);
    setActiveAuthMethod(null);
    window.location.assign(returnPath);
  }, [activeAuthMethod, cookieFirstEnabled, msalInstance, surface, unsubscribeDeviceRegistrations]);

  const getAccessToken = useCallback(async (): Promise<string | null> => {
    if (!msalInstance || !config.enabled || cookieFirstEnabled) return null;

    const account = msalInstance.getActiveAccount();
    if (!account) return null;

    // MSAL already handles cache lookup, refresh-token redemption, iframe
    // fallback, and de-duplicates identical concurrent requests. Retrying a
    // timed-out iframe here only creates more orphaned authorization frames.
    // Do not redirect on failure; the 401 interceptor handles re-auth via
    // auth:unauthorized.
    try {
      const result = await msalInstance.acquireTokenSilent({
        scopes: LOGIN_SCOPES,
        account,
      });
      if (result.idToken) {
        scheduleRefresh(result.idToken);
      }
      return result.idToken;
    } catch (err) {
      console.warn('Silent token acquisition failed:', err);
      if (!isExpectedSilentAuthError(err)) {
        Sentry.captureException(err, {
          tags: { source: 'token_acquisition' },
        });
      }
      return null;
    }
  }, [msalInstance, config.enabled, cookieFirstEnabled, scheduleRefresh]);

  const markNativeAuthenticated = useCallback(() => {
    setNativeAuthed(true);
    setActiveAuthMethod('native');
  }, []);

  const markNativeUnauthenticated = useCallback(() => {
    setNativeAuthed(false);
    setActiveAuthMethod(null);
  }, []);

  // Compute isAuthenticated based on which auth method is active
  const isAuthenticated =
    (activeAuthMethod === 'microsoft' && (cookieFirstEnabled ? microsoftCookieAuthed : !!user)) ||
    (activeAuthMethod === 'native' && nativeAuthed === true) ||
    (!microsoftActive && !nativeActive);  // dev/CI bypass

  const authEnabled = microsoftActive || nativeActive;

  // idle_minutes is advertised by /auth/config; fall back to 60 if absent
  const idleMinutes: number = config.idle_minutes ?? 60;

  return (
    <AuthContext.Provider value={{
      isAuthenticated,
      isLoading,
      authEnabled,
      user,
      login,
      logout,
      logoutIssue,
      clearLogoutIssue: () => setLogoutIssue(null),
      resetDevice,
      getAccessToken,
      authMethods,
      markNativeAuthenticated,
      markNativeUnauthenticated,
      activeAuthMethod,
      nativeRemember,
      idleMinutes,
    }}>
      {children}
    </AuthContext.Provider>
  );
}
